import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import request from 'lib/request';

export interface MentionableUser {
  id: number;
  name: string;
}

/**
 * How many people one piece of text may tag.
 *
 * Mirrors Comments::Mentions::MAX_PER_TEXT, which the write path enforces. Stated here so the
 * composer can say so before a submit rather than after one: the server refuses text over the
 * limit, and being refused for something nothing on screen mentioned is the same trap the
 * title's character limit used to set.
 */
export const MAX_MENTIONS = 25;

/** A name the writer chose, and the account it resolved to. */
export interface ResolvedMention {
  name: string;
  userId: number;
}

/**
 * What goes into the textarea: a name, and nothing else.
 *
 * It used to be the storage marker — `@[Baliwada Yogendra](user:953)` — which meant every
 * person tagging a colleague watched themselves type brackets and an id. The id still
 * decides who is notified and who Jira tags, so it travels beside the text instead of
 * inside it, and Comments::Mentions.embed puts the two back together on arrival.
 *
 * @param {MentionableUser} user The person picked from the dropdown.
 * @return {string} The text to splice in.
 */
export const mentionText = (user: MentionableUser) => `@${user.name}`;

/** A run of the composer's text: prose, or a name that will actually tag somebody. */
export interface MentionSegment {
  text: string;
  userId: number | null;
}

/**
 * Splits the composer's text the same way Comments::Mentions.embed will.
 *
 * The point is that the two agree. embed matches `@#{name}` as a literal string, longest
 * name first, so a name that has since been edited matches nothing and tags nobody — and
 * this has to draw exactly that, or the highlight would promise a tag the server does not
 * send.
 *
 * @param {string} text The whole textarea contents.
 * @param {ResolvedMention[]} mentions Everyone picked from the dropdown so far.
 * @return {MentionSegment[]} Alternating prose and mention runs, in document order.
 */
export const highlightSegments = (
  text: string,
  mentions: ResolvedMention[]
): MentionSegment[] => {
  const claimed: { start: number; end: number; userId: number }[] = [];
  const overlaps = (start: number, end: number) =>
    claimed.some((range) => start < range.end && end > range.start);

  // Longest first, matching embed: "@Jon" must not claim the opening of "@Jonathan Welch"
  // and leave the rest stranded.
  [...mentions]
    .sort((a, b) => b.name.length - a.name.length)
    .forEach(({ name, userId }) => {
      const needle = `@${name}`;
      if (!name) return;

      for (
        let at = text.indexOf(needle);
        at !== -1;
        at = text.indexOf(needle, at + needle.length)
      ) {
        const end = at + needle.length;
        if (!overlaps(at, end)) claimed.push({ start: at, end, userId });
      }
    });

  claimed.sort((a, b) => a.start - b.start);

  const segments: MentionSegment[] = [];
  let cursor = 0;
  claimed.forEach((range) => {
    if (range.start > cursor)
      segments.push({ text: text.slice(cursor, range.start), userId: null });
    segments.push({
      text: text.slice(range.start, range.end),
      userId: range.userId,
    });
    cursor = range.end;
  });
  if (cursor < text.length)
    segments.push({ text: text.slice(cursor), userId: null });

  return segments;
};

/**
 * The `@`-words that look like mentions but will not tag anyone.
 *
 * Typing a colleague's name by hand and never opening the dropdown produces no id, and
 * embed has nothing to match on — so the comment posts, reads as though it tagged them,
 * and notifies nobody. The composer says so rather than letting that pass silently.
 *
 * @param {string} text The whole textarea contents.
 * @param {ResolvedMention[]} mentions Everyone picked from the dropdown so far.
 * @return {string[]} The unresolved tokens, `@` included, without duplicates.
 */
export const unresolvedMentions = (
  text: string,
  mentions: ResolvedMention[]
): string[] => {
  const resolved = new Set(
    highlightSegments(text, mentions)
      .filter((segment) => segment.userId !== null)
      .map((segment) => segment.text)
  );

  const tokens = text.match(/(^|[\s(])@[^\s@]+/g) ?? [];

  return [
    ...new Set(
      tokens
        .map((token) => token.trim().replace(/^\(/, ''))
        // A resolved mention is two words more often than not, so the single word this
        // picked up is the head of one. Anything starting a name that did resolve is fine.
        .filter(
          (token) =>
            !resolved.has(token) &&
            ![...resolved].some((name) => name.startsWith(token))
        )
    ),
  ];
};

/**
 * The token being typed, if the caret is inside one.
 *
 * Anchored to an `@` that starts a word — so an email address does not open the dropdown
 * halfway through typing it, which is the single most common false positive.
 *
 * Names contain a space ("@jason kock"), so the query deliberately runs past the first
 * one. The second ends it: a name is two words here, and by the third the person is writing
 * prose — matching further would leave the dropdown open across a whole sentence.
 *
 * @param {string} text The whole textarea contents.
 * @param {number} caret Current caret offset.
 * @return {Object | null} The `@` offset and the text typed after it, or null.
 */
export const activeQuery = (text: string, caret: number) => {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');

  if (at === -1) return null;
  if (at > 0 && !/[\s(]/.test(before[at - 1])) return null;

  const query = before.slice(at + 1);
  if (query.includes('\n')) return null;
  if ((query.match(/ /g) ?? []).length > 1) return null;
  // A name already inserted is not a token being typed. Two words end the query anyway, so
  // this only matters for the single-word names the rule above would let through.
  if (query.includes(']')) return null;

  return { at, query };
};

/**
 * Matches Query::Users::Mentionable::MIN_LENGTH, which is the rule that actually holds.
 * Kept in step so a single character does not fire a request that can only come back empty.
 */
const MIN_CHARS = 2;
// Exported so a test asserting "past the debounce" reads the number from here rather than
// restating it — a test that hardcoded 150 would quietly stop proving anything the day this
// grew.
export const DEBOUNCE_MS = 150;

/**
 * Drives the @-mention dropdown over a plain textarea.
 *
 * A textarea rather than a rich editor on purpose. Comments are stored as plain text in a
 * column shared with every other commentable thing in the app, so the marker travels in
 * that text — and a contenteditable would mean changing what all of them store to gain
 * styling this does not need.
 *
 * @param {string} value Current textarea contents.
 * @param {Function} onChange Called with the new contents when a mention is inserted.
 * @return {Object} State and handlers for the dropdown.
 */
export function useMentionAutocomplete(
  value: string,
  onChange: (next: string) => void
) {
  const [results, setResults] = useState<MentionableUser[]>([]);
  /*
    Whether the last lookup failed, held apart from the results.

    Both states used to be the same empty array, and the dropdown renders nothing for an
    empty array — so a failed lookup looked exactly like "nobody here is called that". The
    reader then types the name by hand, which produces no id and tags nobody;
    #unresolvedMentions catches that before submit, but it tells them the name is
    unresolved, not that the search never ran. Same shape as the activity feed rendering a
    failed load as "No activity recorded yet."
  */
  const [lookupFailed, setLookupFailed] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [mentions, setMentions] = useState<ResolvedMention[]>([]);
  const [caret, setCaret] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const requestId = useRef(0);

  const token = useMemo(
    () => (isOpen ? activeQuery(value, caret) : null),
    [isOpen, value, caret]
  );

  useEffect(() => {
    if (!token || token.query.length < MIN_CHARS) {
      setLookupFailed(false);
      setResults([]);
      return undefined;
    }

    // Debounced, and every response carries the id of the request that asked for it — a
    // slow early keystroke must not overwrite the results of a later, narrower one.
    const id = ++requestId.current;
    // Hoisted out of the timeout so the cleanup below can actually abort it. It used to be
    // created inside, which meant it was passed to request.post and never triggered: a
    // superseded keystroke's request ran to completion and was discarded on arrival by the
    // id guard alone. The other five fetches in this feature all abort on cleanup.
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const response = await request.post<
        { results: MentionableUser[] },
        unknown
      >(
        '/queries/users/autocomplete',
        { query: { text: token.query } },
        controller
      );

      if (id !== requestId.current) return;

      // A lookup the reader walked away from, cancelled by the cleanup below. It says
      // nothing about whether the endpoint works, and announcing it would put an error
      // under a comment box they have already cleared.
      //
      // Asked of the signal rather than of response.type, which is what this used to read.
      // lib/request maps a cancelled fetch to 'abort' only when the thrown DOMException is
      // `instanceof Error` — true in browsers, false under jsdom, where the same
      // cancellation arrives as 'fatal' and was reported as a failure. The signal is the
      // thing we actually know.
      if (controller.signal.aborted) return;

      setLookupFailed(response.type !== 'success');
      setResults(
        response.type === 'success' ? (response.data?.results ?? []) : []
      );
      setHighlighted(0);
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [token]);

  /*
    Stable across renders, because a consumer depends on its identity.

    ReportFormModal registers a capture-phase Escape listener while this list is open and
    lists `close` in the effect's dependencies. Rebuilt as a fresh arrow on every render, that
    effect tore the listener down and re-added it on every keystroke — harmless but pointless
    churn, and the kind of dependency that makes an effect's lifetime hard to reason about.
  */
  const close = useCallback(() => setIsOpen(false), []);

  const insert = useCallback(
    (user: MentionableUser) => {
      const active = activeQuery(value, caret);
      if (!active) return;

      const next = `${value.slice(0, active.at)}${mentionText(
        user
      )} ${value.slice(caret)}`;
      onChange(next);

      // Remembered because the text no longer carries the id. Keyed by name so picking the
      // same person twice does not send them twice, and left in place if the writer later
      // deletes the name — embed matches on the text, so a stale entry simply tags nobody.
      setMentions((prev) => {
        if (prev.some((m) => m.name === user.name)) return prev;

        return [...prev, { name: user.name, userId: user.id }];
      });

      setIsOpen(false);
      setResults([]);
    },
    [value, caret, onChange]
  );

  return {
    /** Options to show. Empty means the dropdown is not rendered. */
    results: token ? results : [],
    highlighted,
    setHighlighted,
    insert,
    /**
     * Everyone picked from the dropdown, to send alongside the text.
     *
     * The server reassembles the markers from these; without them the names are just words
     * and nobody is notified.
     */
    mentions,
    /**
     * Replace what has been resolved so far.
     *
     * Called with nothing after a successful submit, so the next comment starts with nobody
     * tagged. Called with a list when a field is seeded from text that already carries
     * mentions — editing a description, where the textarea shows the readable projection and
     * the ids have to be carried alongside it or the save silently drops them.
     *
     * @param {ResolvedMention[]} next The mentions to hold; defaults to none.
     * @return {void}
     */
    resetMentions: (next: ResolvedMention[] = []) => setMentions(next),
    /**
     * Call from the textarea on every change, selection move, and click.
     *
     * @param {number} position The caret offset to track.
     */
    syncCaret: (position: number) => {
      setCaret(position);
      setIsOpen(true);
    },
    /**
     * Call from the textarea's onKeyUp, in place of syncCaret.
     *
     * Every key that moves the caret without changing the text has to be followed, which is
     * why the caret is synced on keyup at all. Escape is the one key that does neither: it
     * dismisses. Routing it through syncCaret reopened what onKeyDown had just closed —
     * setIsOpen(true) put `token` back, the effect refired, and the list came back 150 ms
     * later, so Escape looked like a flicker rather than a dismissal.
     *
     * A handler here rather than an early return written into each textarea, because there
     * are three of them and two had the bug. The consumer that syncs from onSelect instead
     * never did — Escape moves no caret, so onSelect does not fire.
     *
     * @param {React.KeyboardEvent<HTMLTextAreaElement>} event The keyup being handled.
     * @return {void}
     */
    syncCaretFromKeyUp: (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Escape') return;

      setCaret(event.currentTarget.selectionStart ?? 0);
      setIsOpen(true);
    },
    close,
    /** True when the last lookup failed, so the dropdown can say so. */
    lookupFailed,
  };
}
